import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Users, Mail, Calendar, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SubscribersManagement = () => {
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscribers, setSubscribers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  const loadSubscribers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/subscribers`, {
        headers: getAuthHeader()
      });
      setSubscribers(response.data);
    } catch (error) {
      toast.error('Failed to load subscribers');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    loadSubscribers();
  }, [loadSubscribers]);

  const totalRevenue = useMemo(() =>
    subscribers.reduce((sum, s) => sum + s.amountPaid, 0).toFixed(2),
    [subscribers]
  );

  const marketingConsentCount = useMemo(() =>
    subscribers.filter(s => s.marketingConsent).length,
    [subscribers]
  );

  const filteredSubscribers = useMemo(() => subscribers.filter(sub =>
    sub.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sub.email.toLowerCase().includes(searchTerm.toLowerCase())
  ), [subscribers, searchTerm]);

  const exportToCSV = () => {
    const headers = ['Name', 'Email', 'Type', 'Amount Paid', 'Start Date', 'End Date', 'Status', 'Marketing Consent'];
    const rows = filteredSubscribers.map(sub => [
      sub.name,
      sub.email,
      sub.subscriptionType,
      sub.amountPaid,
      new Date(sub.subscriptionStart).toLocaleDateString(),
      sub.subscriptionEnd ? new Date(sub.subscriptionEnd).toLocaleDateString() : 'N/A',
      sub.isActive ? 'Active' : 'Inactive',
      sub.marketingConsent ? 'Yes' : 'No'
    ]);

    const csv = [headers, ...rows].map(row => row.join(',')).join('\\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    toast.success('Subscribers exported!');
  };

  const getSubscriptionLabel = (type) => {
    const labels = {
      single_book: { text: 'Single Book', color: 'bg-blue-100 text-blue-800' },
      all_books: { text: 'All Books', color: 'bg-purple-100 text-purple-800' },
      purchase: { text: 'Purchase', color: 'bg-green-100 text-green-800' }
    };
    return labels[type] || labels.purchase;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">Subscribers</h1>
            <p className="text-gray-600 mt-2">
              {subscribers.length} total subscribers • {subscribers.filter(s => s.marketingConsent).length} opted in for marketing
            </p>
          </div>
          <Button onClick={exportToCSV} variant="outline">
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>

        {/* Search */}
        <div className="mb-6">
          <Input
            placeholder="Search subscribers by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="max-w-md"
          />
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Subscribers</p>
                  <p className="text-2xl font-bold text-gray-800">{subscribers.length}</p>
                </div>
                <Users className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active</p>
                  <p className="text-2xl font-bold text-green-600">
                    {subscribers.filter(s => s.isActive).length}
                  </p>
                </div>
                <Calendar className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Marketing Consent</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {marketingConsentCount}
                  </p>
                </div>
                <Mail className="h-8 w-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Revenue</p>
                  <p className="text-2xl font-bold text-green-600">
                    ${totalRevenue}
                  </p>
                </div>
                <Download className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscribers Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Marketing</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredSubscribers.map((subscriber) => {
                    const typeLabel = getSubscriptionLabel(subscriber.subscriptionType);
                    return (
                      <tr key={subscriber.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-800">{subscriber.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{subscriber.email}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${typeLabel.color}`}>
                            {typeLabel.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-800">${subscriber.amountPaid.toFixed(2)}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">
                          {subscriber.subscriptionEnd
                            ? new Date(subscriber.subscriptionEnd).toLocaleDateString()
                            : 'N/A'}
                        </td>
                        <td className="px-6 py-4">
                          {subscriber.isActive ? (
                            <span className="text-green-600 font-medium">Active</span>
                          ) : (
                            <span className="text-red-600 font-medium">Inactive</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {subscriber.marketingConsent ? (
                            <span className="text-green-600">✓ Yes</span>
                          ) : (
                            <span className="text-gray-400">✗ No</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {filteredSubscribers.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No subscribers found.
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscribersManagement;
